import { Checkbox, Divider, FormControlLabel, List, ListItem, Paper, Typography } from '@mui/material';
import { MemreFlexBox, MemreText } from 'components/core';
import { useAuthoringContext } from 'pages/authoring/AuthoringContext';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { IItem } from 'models/item';
import { useAssignmentItems } from 'hooks/useAssignmentItems';
import { normalizeJSONAPIResponse } from 'utils/modelUtils';

interface AttachInstructionalItemProps {
  itemId: string;
  onChange?: (attachedItemIds: string[]) => void;
}

export default function AttachInstructionalItem({ itemId, onChange }: AttachInstructionalItemProps) {
  const { set } = useAuthoringContext();
  const [attachedItemIds, setAttachedItemIds] = useState<string[]>([]);
  const [isLoadingAttachedItems, setIsLoadingAttachedItems] = useState<boolean>(true);

  // Use the same hook that AttachIc uses to get assignment items
  const { data: itemsData = [], isLoading: isLoadingInstructionalItems } = useAssignmentItems(set?.id || '');

  // Filter to only include studiable instructional content
  const instructionalItems = itemsData.filter(
    (item: IItem) => item.templateType === 'instructional_contents' && item.isStudiable === true
  );

  /**
   * Fetches attached items from the API and updates state
   */
  const fetchAttachedItems = useCallback(async () => {
    if (!itemId) return;

    try {
      setIsLoadingAttachedItems(true);

      // Fetch the current item to see if it has attached items
      const response = await axios.get(`/api/v3/items/${itemId}?include=attached_items`);

      // Use normalizeJSONAPIResponse to properly format the API response
      const normalizedData = normalizeJSONAPIResponse(response.data);

      // Extract attached items from the normalized data
      if (normalizedData.attachedItems && normalizedData.attachedItems.length > 0) {
        const attachedIds = normalizedData.attachedItems.map((item: IItem) => item.id);
        setAttachedItemIds(attachedIds);
      } else {
        setAttachedItemIds([]);
      }
    } catch (error) {
      console.error('Error fetching attached items', error);
    } finally {
      setIsLoadingAttachedItems(false);
    }
  }, [itemId]);

  // Initialize attached items when component mounts or itemId changes
  useEffect(() => {
    fetchAttachedItems();
  }, [fetchAttachedItems]);

  /**
   * Toggles an instructional item's attachment status
   */
  const handleToggleItem = async (toggledItemId: string) => {
    try {
      const isCurrentlyAttached = attachedItemIds.includes(toggledItemId);

      if (isCurrentlyAttached) {
        // Remove the item
        await axios.delete(`/api/v3/items/${itemId}/attached_items/${toggledItemId}`);

        // Update local state
        const newAttachedIds = attachedItemIds.filter((id) => id !== toggledItemId);
        setAttachedItemIds(newAttachedIds);

        if (onChange) {
          onChange(newAttachedIds);
        }
      } else {
        // Add the item
        await axios.post(`/api/v3/items/${itemId}/attached_items`, {
          data: {
            attributes: {
              attached_item_id: toggledItemId,
            },
          },
        });

        // Update local state
        const newAttachedIds = [...attachedItemIds, toggledItemId];
        setAttachedItemIds(newAttachedIds);

        if (onChange) {
          onChange(newAttachedIds);
        }
      }

      // Refresh from API to ensure UI is in sync with backend
      await fetchAttachedItems();
    } catch (error) {
      console.error('Error toggling instructional item attachment', error);
    }
  };

  if (isLoadingInstructionalItems || isLoadingAttachedItems) {
    return (
      <MemreFlexBox direction="column" gap={2} sx={{ mt: 2 }}>
        <MemreText>Loading instructional items...</MemreText>
      </MemreFlexBox>
    );
  }

  if (instructionalItems.length === 0) {
    return (
      <MemreFlexBox direction="column" gap={2} sx={{ mt: 2 }}>
        <MemreText>
          Looks like you haven’t added an instructional item yet. Add one to the set first, then you can attach it here.
        </MemreText>
      </MemreFlexBox>
    );
  }

  return (
    <MemreFlexBox direction="column" gap={2} sx={{ mt: 2 }}>
      <Typography variant="subtitle1">Attach Instructional Item</Typography>
      <Typography variant="body2" color="textSecondary">
        If you have relevant instructional items you can attach them to this item. Learners will see this after
        submitting their answer.
      </Typography>

      <Paper
        variant="outlined"
        sx={{
          maxHeight: '300px',
          overflow: 'auto',
          width: '100%',
        }}
      >
        <List>
          {instructionalItems.map((item, index) => {
            const isChecked = attachedItemIds.includes(item.id);

            return (
              <React.Fragment key={item.id}>
                <ListItem disablePadding sx={{ px: 2, py: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox checked={isChecked} onChange={() => handleToggleItem(item.id)} color="primary" />
                    }
                    label={item.title || 'Untitled'}
                    sx={{ width: '100%' }}
                  />
                </ListItem>
                {index < instructionalItems.length - 1 && <Divider component="li" />}
              </React.Fragment>
            );
          })}
        </List>
      </Paper>
    </MemreFlexBox>
  );
}
